import React, { useEffect, useCallback, useRef, useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import CloseFullscreenRoundedIcon from "@mui/icons-material/CloseFullscreenRounded";

/**
 * Unified Overlay component — replaces the repeated backdrop + content
 * wrapper pattern found across 60+ overlay files in the codebase.
 *
 * Provides:
 *  - Dark semi-transparent backdrop (click-to-close optional)
 *  - Responsive content container (full-screen on mobile)
 *  - Header with back button + title + optional select + action buttons
 *  - Optional bookmark-style tabs (right-side "book tabs") or standard tabs
 *  - Scrollable content area (children or active tab content)
 *  - Optional warning/info bars below content
 *  - Optional footer with buttons
 *  - ESC key to close
 *  - Proper z-index management
 *
 * Usage:
 *   <Overlay
 *     open={showOverlay}
 *     onClose={() => setOverlayName("")}
 *     title="New Interpretation"
 *     headerActions={<Button>Save</Button>}
 *     tabs={[{ label: "Details", content: <DetailsTab /> }, ...]}
 *     warnings={[{ type: "warning", message: "Timezone mismatch" }]}
 *     headerSelect={<Select>...</Select>}
 *     footer={<Box>...</Box>}
 *   >
 *     {children}
 *   </Overlay>
 */
const Overlay = ({
  open,
  onClose,
  title,
  titleIcon,
  headerActions,
  children,
  footer,
  maxWidth = "lg",
  fullScreenOnMobile = true,
  closeOnBackdrop = false,
  closeOnEscape = true,
  zIndex = 1300,
  backdropColor = "rgba(0, 0, 0, 0.8)",
  disableBackdropClick = true,
  // Allow consumers to pass extra sx to the content container
  contentSx,
  // Allow consumers to pass extra sx to the backdrop
  backdropSx,
  // Allow consumers to pass extra sx to the header
  headerSx,
  // Back button props — pass null/undefined to hide the back button
  backButtonProps,
  // When true, the back button is shown (default: true when onClose is provided)
  showBackButton,
  // ── New slots ──────────────────────────────────────────────────────
  // Tabs: array of { label, icon?, content } OR a ReactNode for fully custom rendering.
  // When provided as array, bookmark-style tabs appear on the right edge (desktop)
  // or horizontal scrollable tabs (mobile). Active tab's content replaces children.
  tabs,
  // Controlled active tab index (optional — if omitted, internal state is used)
  activeTab,
  // Callback when active tab changes: (newIndex) => void
  onTabChange,
  // Warnings: array of { type: 'info' | 'warning' | 'error', message, icon? }
  // Rendered as colored bars below the content area (before footer).
  warnings,
  // Header select: a ReactNode (e.g. <FormControl><Select>...</Select></FormControl>)
  // Rendered in the header between the title and headerActions.
  headerSelect,
  // ── Extended props ─────────────────────────────────────────────────
  // Subtitle: rendered below the title in the header, smaller text
  subtitle,
  // maxWidthPx: number — override the default max-width pixel values
  maxWidthPx,
  // height: string — control content height strategy
  //   "fit-content" | "75%" | "90vh" | "auto"
  //   Default: current responsive behavior { xs: "95%", sm: "75%" }
  height,
  // contentBg: "alt" | "default" — controls background palette
  //   Default: "default" (white overlay, grey header/footer)
  contentBg = "default",
  // scrollable: boolean — whether content area scrolls. Default: true
  scrollable = true,
  // footerSx: object — extra sx for the footer Box
  footerSx,
  // ── Phase 2: Drag, Resize, Maximize ───────────────────────────────
  // draggable: boolean — enable dragging by header. Default: true
  draggable = true,
  // maximizable: boolean — show maximize/restore button. Default: true
  maximizable = true,
  // persistState: boolean — save position/size/maximized to localStorage. Default: true
  persistState = true,
  // id: string — unique key for localStorage persistence (REQUIRED if persistState is true)
  id,
}) => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const contentRef = useRef(null);
  const headerRef = useRef(null);
  const [internalTab, setInternalTab] = useState(0);

  // ── Initialize drag state from localStorage ──
  const getInitialDragOffset = () => {
    if (!persistState || !id || isMobile) return { x: 0, y: 0 };
    try {
      const saved = localStorage.getItem(`ce_portal.overlay.${id}`);
      if (saved) {
        const state = JSON.parse(saved);
        if (state.dragOffset) return state.dragOffset;
      }
    } catch (e) {
      // ignore parse errors
    }
    return { x: 0, y: 0 };
  };

  const getInitialMaximized = () => {
    if (!persistState || !id || isMobile) return false;
    try {
      const saved = localStorage.getItem(`ce_portal.overlay.${id}`);
      if (saved) {
        const state = JSON.parse(saved);
        if (state.isMaximized !== undefined) return state.isMaximized;
      }
    } catch (e) {
      // ignore parse errors
    }
    return false;
  };

  // ── Drag state ──
  const [dragOffset, setDragOffset] = useState(getInitialDragOffset);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });

  // ── Maximize state ──
  const [isMaximized, setIsMaximized] = useState(getInitialMaximized);

  // ── Save state to localStorage ──
  const saveState = useCallback(() => {
    if (!persistState || !id || isMobile) return;
    try {
      localStorage.setItem(
        `ce_portal.overlay.${id}`,
        JSON.stringify({ dragOffset, isMaximized, version: 1 })
      );
    } catch (e) {
      // ignore storage errors
    }
  }, [persistState, id, dragOffset, isMaximized, isMobile]);

  // Save on state change (debounced via effect)
  useEffect(() => {
    const timer = setTimeout(saveState, 300);
    return () => clearTimeout(timer);
  }, [dragOffset, isMaximized, saveState]);

  // ── Clamp drag offset to keep overlay within viewport ──
  const clampOffset = useCallback((x, y) => {
    const el = contentRef.current;
    const elW = el ? el.offsetWidth : 800;
    const elH = el ? el.offsetHeight : 600;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const maxX = (vw - elW) / 2 - 10;
    const maxY = (vh - elH) / 2 - 10;
    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  }, []);

  // ── Drag handlers ──
  const handleDragStart = useCallback(
    (e) => {
      if (!draggable || isMaximized || isMobile) return;
      // Only drag from left-click on header
      if (e.button !== 0) return;
      e.preventDefault();
      const startX = e.clientX;
      const startY = e.clientY;
      dragStartRef.current = {
        x: startX,
        y: startY,
        offsetX: dragOffset.x,
        offsetY: dragOffset.y,
      };
      setIsDragging(true);

      const handleMouseMove = (moveEvent) => {
        const dx = moveEvent.clientX - startX;
        const dy = moveEvent.clientY - startY;
        const raw = {
          x: dragStartRef.current.offsetX + dx,
          y: dragStartRef.current.offsetY + dy,
        };
        setDragOffset(clampOffset(raw.x, raw.y));
      };

      const handleMouseUp = () => {
        setIsDragging(false);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [draggable, isMaximized, isMobile, dragOffset, clampOffset]
  );

  // ── Touch drag handlers (mobile) ──
  const handleTouchStart = useCallback(
    (e) => {
      if (!draggable || isMaximized || isMobile) return;
      const touch = e.touches[0];
      const startX = touch.clientX;
      const startY = touch.clientY;
      dragStartRef.current = {
        x: startX,
        y: startY,
        offsetX: dragOffset.x,
        offsetY: dragOffset.y,
      };
      setIsDragging(true);

      const handleTouchMove = (moveEvent) => {
        const t = moveEvent.touches[0];
        const dx = t.clientX - startX;
        const dy = t.clientY - startY;
        const raw = {
          x: dragStartRef.current.offsetX + dx,
          y: dragStartRef.current.offsetY + dy,
        };
        setDragOffset(clampOffset(raw.x, raw.y));
      };

      const handleTouchEnd = () => {
        setIsDragging(false);
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };

      document.addEventListener("touchmove", handleTouchMove);
      document.addEventListener("touchend", handleTouchEnd);
    },
    [draggable, isMaximized, isMobile, dragOffset]
  );

  // ── Maximize toggle ──
  const handleMaximizeToggle = useCallback(() => {
    setIsMaximized((prev) => !prev);
  }, []);

  // Resolve active tab: controlled (activeTab prop) or internal state
  const tabsArray = Array.isArray(tabs) ? tabs : null;
  const currentTabIndex = activeTab !== undefined ? activeTab : internalTab;
  const handleTabChange = useCallback(
    (newIndex) => {
      if (onTabChange) onTabChange(newIndex);
      else setInternalTab(newIndex);
    },
    [onTabChange],
  );

  // --- ESC key handler ---
  const handleKeyDown = useCallback(
    (e) => {
      if (closeOnEscape && e.key === "Escape") {
        e.stopPropagation();
        onClose?.();
      }
    },
    [closeOnEscape, onClose],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  // --- Backdrop click ---
  const handleBackdropClick = useCallback(
    (e) => {
      if (closeOnBackdrop && e.target === e.currentTarget) {
        onClose?.();
      }
    },
    [closeOnBackdrop, onClose],
  );

  // --- Prevent content clicks from reaching backdrop ---
  const handleContentClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // --- Determine if back button should show ---
  const shouldShowBackButton =
    showBackButton !== undefined ? showBackButton : Boolean(onClose);

  // --- Responsive width map ---
  const defaultMaxWidthPx = {
    sm: 600,
    md: 900,
    lg: 1200,
    xl: 1500,
  };

  // Resolve effective max-width: explicit override or from the size map
  const resolvedMaxWidthPx = maxWidthPx ?? (defaultMaxWidthPx[maxWidth] || 1200);

  // When maxWidthPx is explicitly provided the caller has declared the intended
  // size cap — let the width grow to it instead of being held back by the
  // percentage map (60% at lg = 1152px on a 1920px screen, which silently
  // capped wide overlays well below their maxWidthPx).
  const hasExplicitMaxWidthPx = maxWidthPx !== undefined;

  // Resolve effective background color from contentBg prop
  // "default" = grey overlay with white content area (new default)
  // "alt" = grey overlay with white content area (legacy — same thing)
  const resolvedBg = theme.palette.background.alt;

  // Resolve effective height
  const isFitContent = height === "fit-content";
  const resolvedHeight = height
    ? height
    : {
        xs: fullScreenOnMobile ? "100%" : "95%",
        sm: "75%",
      };

  // --- Warning type → color mapping ---
  const warningColors = {
    info: {
      bg: theme.palette.background.info,
      border: theme.palette.info?.main || theme.palette.primary.main,
      text: theme.palette.primary.text,
    },
    warning: {
      bg:
        theme.palette.background.warning ||
        theme.palette.warning.main + "20",
      border: theme.palette.warning.main,
      text:
        theme.palette.warning.contrastText || theme.palette.text.primary,
    },
    error: {
      bg:
        theme.palette.background.error || theme.palette.error.main + "20",
      border: theme.palette.error.main,
      text: theme.palette.error.main,
    },
  };

  if (!open) return null;

  // ── Semantic field borders (applied to every overlay) ──────────────
  // Filled field → primary (green/teal); empty stays grey (theme default);
  // hover/focus keep their theme colors. `:where()` keeps specificity at
  // (0,3,0): above the theme's base rules, below per-field sx (0,3,1) — so
  // overlays with their own border logic (blue = changed / red = required,
  // e.g. service overlays) are untouched.
  // `:has()` requires Chrome/Edge 105+, Safari 15.4+, Firefox 121+.
  const filledFieldBorderSx = {
    // Text fields → live update (InputBase drives FormControl.filled).
    // Selects & multi-Autocompletes → detected on mount/value change reveal.
    "& :where(.MuiFormControl-root):has(.MuiFormLabel-filled) .MuiOutlinedInput-notchedOutline":
      {
        borderColor: theme.palette.primary.main,
      },
    // Multiple-Autocomplete selections render as chips while the inner
    // input stays empty — treat selected chips as "filled".
    "& :where(.MuiAutocomplete-root):has(.MuiChip-root) .MuiOutlinedInput-notchedOutline":
      {
        borderColor: theme.palette.primary.main,
      },
    // Error fields keep MUI's red (same specificity as the rules above,
    // declared later so it wins when a field is both filled and in error).
    "& .MuiOutlinedInput-root.Mui-error :where(.MuiOutlinedInput-notchedOutline)":
      {
        borderColor: theme.palette.error.main,
      },
  };

  // --- Bookmark tab styles (desktop) ---
  const bookmarkTabWidth = 44;
  const bookmarkTabHeight = 56;
  const bookmarkTabGap = 4;
  const bookmarkTabBorderRadius = "0 12px 12px 0";

  return (
    <Box
      onClick={closeOnBackdrop ? handleBackdropClick : undefined}
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: backdropColor,
        zIndex: zIndex,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        ...backdropSx,
      }}
    >
      <Box
        ref={contentRef}
        onClick={handleContentClick}
        sx={{
          bgcolor: resolvedBg,
          width: isMaximized
            ? "100%"
            : hasExplicitMaxWidthPx
              ? {
                  // Explicit maxWidthPx → let the pixel cap bind.
                  xs: "100%",
                  sm: "92%",
                  md: "92%",
                  lg: "92%",
                }
              : {
                  xs: fullScreenOnMobile ? "100%" : "100%",
                  sm: "90%",
                  md: maxWidth === "xl" ? "75%" : maxWidth === "md" ? "85%" : "75%",
                  lg: maxWidth === "xl" ? "60%" : maxWidth === "md" ? "75%" : "60%",
                },
          height: isMaximized
            ? "100%"
            : resolvedHeight,
          minWidth: isMaximized
            ? "100%"
            : {
                xs: "100%",
                sm: "600px",
              },
          maxWidth: isMaximized ? "100%" : resolvedMaxWidthPx,
          maxHeight: isMaximized
            ? "100%"
            : isFitContent
              ? { xs: "95%", sm: "90%" }
              : undefined,
          display: "flex",
          borderRadius:
            fullScreenOnMobile && isMobile ? 0 : isMaximized ? 0 : "1.5rem",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: "column",
          color: theme.palette.text.primary,
          overflow: "hidden",
          // ── Drag transform (clamped inline to prevent off-screen) ──
          transform: (() => {
            if (!draggable || isMaximized || isMobile) return undefined;
            const el = contentRef.current;
            const elW = el ? el.offsetWidth : 800;
            const elH = el ? el.offsetHeight : 600;
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            // Max X: overlay's right edge stays within viewport (20px margin)
            // Max Y: overlay's bottom edge stays within viewport (20px margin)
            const maxX = (vw - elW) / 2 - 10;
            const maxY = (vh - elH) / 2 - 10;
            const cx = Math.max(-maxX, Math.min(maxX, dragOffset.x));
            const cy = Math.max(-maxY, Math.min(maxY, dragOffset.y));
            return `translate(${cx}px, ${cy}px)`;
          })(),
          transition: isDragging ? "none" : "transform 0.2s ease-out, width 0.3s ease, height 0.3s ease, border-radius 0.3s ease",
          // ── Position relative for drag offset ──
          position: "relative",
          ...contentSx,
        }}
      >
        {/* ── Header ── */}
        <Box
          ref={headerRef}
          onMouseDown={handleDragStart}
          onTouchStart={handleTouchStart}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            flexDirection: "row",
            py: ".75rem",
            px: "2rem",
            flexShrink: 0,
            minHeight: "56px",
            // ── Drag cursor ──
            cursor:
              draggable && !isMaximized && !isMobile
                ? isDragging
                  ? "grabbing"
                  : "grab"
                : "default",
            userSelect: "none",
            ...headerSx,
          }}
        >
          {/* Left side: back button + title */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              flexDirection: "row",
              flex: 1,
              minWidth: 0,
            }}
          >
            {shouldShowBackButton && (
              <IconButton
                color="primary"
                size="large"
                onClick={onClose}
                sx={{ flexShrink: 0 }}
                {...(backButtonProps || {})}
              >
                <ChevronLeftRoundedIcon fontSize="inherit" />
              </IconButton>
            )}

            {subtitle ? (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant={isMobile ? "h4" : "h2"}
                  fontWeight="bold"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {subtitle}
                </Typography>
              </Box>
            ) : (
              <Typography
                variant={isMobile ? "h4" : "h2"}
                fontWeight="bold"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </Typography>
            )}
          </Box>

          {/* Center: optional header select */}
          {headerSelect && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                mx: 2,
              }}
            >
              {headerSelect}
            </Box>
          )}

          {/* Right side: maximize button + optional action buttons */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              gap: 1,
            }}
          >
            {/* Maximize/Restore button */}
            {maximizable && !isMobile && (
              <IconButton
                color="primary"
                size="small"
                onClick={handleMaximizeToggle}
                sx={{ flexShrink: 0 }}
                title={isMaximized ? "Restore" : "Maximize"}
              >
                {isMaximized ? (
                  <CloseFullscreenRoundedIcon fontSize="small" />
                ) : (
                  <OpenInFullRoundedIcon fontSize="small" />
                )}
              </IconButton>
            )}
            {headerActions && headerActions}
          </Box>
        </Box>

        {/* ── Tabs area ── */}
        {tabsArray && !isMobile && (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              flex: 1,
              minHeight: 0,
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Horizontal tab bar at top */}
            <Box
              sx={{
                display: "flex",
                width: "100%",
                flexShrink: 0,
                pt: "0.5rem",
                pl: "1.5rem",
                gap: 0.5,
              }}
            >
              {tabsArray.map((tab, index) => {
                const isActive = index === currentTabIndex;
                return (
                  <Box
                    key={index}
                    onClick={() => handleTabChange(index)}
                    sx={{
                      px: 2.5,
                      py: 1,
                      cursor: "pointer",
                      borderRadius: "12px 12px 0 0",
                      bgcolor: isActive
                        ? theme.palette.background.default
                        : theme.palette.grey[300],
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        bgcolor: isActive
                          ? theme.palette.background.default
                          : theme.palette.grey[400],
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {tab.icon && (
                        <Box
                          sx={{
                            color: isActive
                              ? theme.palette.primary.main
                              : theme.palette.text.secondary,
                            display: "flex",
                            fontSize: "1.1rem",
                          }}
                        >
                          {tab.icon}
                        </Box>
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isActive ? 700 : 500,
                          color: isActive
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {tab.label}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Content area (scrollable) */}
            <Box
              sx={{
                position: "relative",
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* ── Warning background colors (behind white content) ── */}
              {warnings && warnings.length > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: "100%",
                    height: "50%",
                    display: "flex",
                    flexDirection: "column-reverse",
                    zIndex: 0,
                  }}
                >
                  {warnings.map((warning, index) => {
                    const colors = warningColors[warning.type] || warningColors.info;
                    return (
                      <Box
                        key={index}
                        sx={{
                          flex: 1,
                          width: "100%",
                          backgroundColor: colors.bg,
                        }}
                      />
                    );
                  })}
                </Box>
              )}
              {/* ── Inner content wrapper ── */}
              <Box
                sx={{
                  position: "relative",
                  flex: 1,
                  minHeight: 0,
                  width: "100%",
                  overflowY: scrollable ? "auto" : "visible",
                  overflowX: "hidden",
                  backgroundColor: theme.palette.background.default,
                  borderRadius: "1.5rem",
                  zIndex: 1,
                  ...filledFieldBorderSx,
                }}
              >
                {tabsArray[currentTabIndex]?.content ?? children}
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Tabs area (mobile) — horizontal scrollable tabs ── */}
        {tabsArray && isMobile && (
          <Box
            sx={{
              display: "flex",
              width: "100%",
              flex: 1,
              minHeight: 0,
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Horizontal tab bar */}
            <Box
              sx={{
                display: "flex",
                overflowX: "auto",
                flexShrink: 0,
                borderBottom: `1px solid ${theme.palette.divider}`,
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
              }}
            >
              {tabsArray.map((tab, index) => {
                const isActive = index === currentTabIndex;
                return (
                  <Box
                    key={index}
                    onClick={() => handleTabChange(index)}
                    sx={{
                      px: 2.5,
                      py: 1,
                      cursor: "pointer",
                      borderBottom: isActive
                        ? `2px solid ${theme.palette.primary.main}`
                        : "2px solid transparent",
                      flexShrink: 0,
                      transition: "all 0.2s ease-in-out",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      {tab.icon && (
                        <Box
                          sx={{
                            color: isActive
                              ? theme.palette.primary.main
                              : theme.palette.text.secondary,
                            display: "flex",
                            fontSize: "1.1rem",
                          }}
                        >
                          {tab.icon}
                        </Box>
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isActive ? 700 : 500,
                          color: isActive
                            ? theme.palette.primary.main
                            : theme.palette.text.secondary,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {tab.label}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Mobile tab content */}
            <Box
              sx={{
                position: "relative",
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* ── Inner content wrapper ── */}
              <Box
                sx={{
                  position: "relative",
                  flex: 1,
                  minHeight: 0,
                  width: "100%",
                  overflowY: scrollable ? "auto" : "visible",
                  overflowX: "hidden",
                  backgroundColor: theme.palette.background.default,
                  borderRadius: "1.5rem",
                  ...filledFieldBorderSx,
                }}
              >
                {tabsArray[currentTabIndex]?.content ?? children}
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Content area (scrollable) — when no tabs ── */}
        {!tabsArray && (
          <Box
            sx={{
              position: "relative",
              flex: 1,
              minHeight: 0,
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* ── Warning background colors (behind white content) ── */}
            {warnings && warnings.length > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: "100%",
                  height: "50%",
                  display: "flex",
                  flexDirection: "column-reverse",
                  zIndex: 0,
                }}
              >
                {warnings.map((warning, index) => {
                  const colors = warningColors[warning.type] || warningColors.info;
                  return (
                    <Box
                      key={index}
                      sx={{
                        flex: 1,
                        width: "100%",
                        backgroundColor: colors.bg,
                      }}
                    />
                  );
                })}
              </Box>
            )}
            {/* ── Inner content wrapper ── */}
            <Box
              sx={{
                position: "relative",
                flex: 1,
                minHeight: 0,
                overflowY: scrollable ? "auto" : "visible",
                overflowX: "hidden",
                backgroundColor: theme.palette.background.default,
                borderRadius: "1.5rem",
                width: "100%",
                zIndex: 1,
                ...filledFieldBorderSx,
              }}
            >
              {children}
            </Box>
          </Box>
        )}

        {/* ── Warnings area ── */}
        {warnings && warnings.length > 0 && (
          <Box
            sx={{
              width: "100%",
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              // ── Use last warning's color as background to fill rounded corner gaps ──
              backgroundColor:
                warningColors[warnings[warnings.length - 1]?.type]?.bg ||
                "transparent",
              borderRadius: fullScreenOnMobile && isMobile ? 0 : isMaximized ? 0 : "0 0 1.5rem 1.5rem",
              overflow: "hidden",
            }}
          >
            {warnings.map((warning, index) => {
              const colors =
                warningColors[warning.type] || warningColors.info;
              return (
                <Box
                  key={index}
                  sx={{
                    width: "100%",
                    minHeight: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: colors.bg,
                    borderBottom:
                      index < warnings.length - 1
                        ? `1px solid ${colors.border}`
                        : "none",
                    borderRadius:
                      index === warnings.length - 1
                        ? "0 0 1.5rem 1.5rem"
                        : "0",
                    px: 2,
                    py: 0.75,
                    gap: 1,
                  }}
                >
                  {warning.icon && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        color: colors.border,
                        fontSize: "1.1rem",
                        flexShrink: 0,
                      }}
                    >
                      {warning.icon}
                    </Box>
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text,
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      textAlign: "center",
                      whiteSpace: "pre-line",
                    }}
                  >
                    {warning.message}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}

        {/* ── Footer (optional) ── */}
        {footer && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              flexDirection: "row",
              py: ".5rem",
              px: "2rem",
              flexShrink: 0,
              minHeight: "52px",
              ...footerSx,
            }}
          >
            {footer}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Overlay;
